import { useGetAccount } from "@/queries/account-queries";
import { useEffect, useRef } from "react";

const pylonUnauthenticatedUserIdKey = "pylon-unauthenticated-user-id";

const disableChat = process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";

export const useBootInAppChat = () => {
    const APP_ID = "efa0bf58-5742-41dd-81e3-ffcb7cccda75";

    const { data: account } = useGetAccount();

    // For deduplication.
    const calledOnce = useRef(false);

    useEffect(() => {
        if (disableChat) {
            return;
        }
        if (calledOnce.current) {
            return;
        }
        calledOnce.current = true;
        const script = document.createElement("script");
        script.setAttribute("type", "text/javascript");
        script.setAttribute("async", "true");
        script.setAttribute("src", `https://widget.usepylon.com/widget/${APP_ID}`);
        script.onload = () => {
            disableAnimations().catch(console.error);
        };
        script.onerror = (error) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const err = error instanceof ErrorEvent ? error.error : error;
            console.log("Not booting Pylon: failed to inject script", err);
        };
        document.head.insertBefore(script, document.head.firstChild);

        return () => {
            if (document.head.contains(script)) {
                try {
                    document.head.removeChild(script);
                } catch (error) {
                    console.error("Failed to remove Pylon script", error);
                }
            }
        };
    }, []);

    useEffect(() => {
        if (disableChat) {
            return;
        }
        if (!account) {
            if (!window.localStorage.getItem(pylonUnauthenticatedUserIdKey)) {
                window.localStorage.setItem(pylonUnauthenticatedUserIdKey, crypto.randomUUID());
            }
            return;
        }
    }, [account]);

    useEffect(() => {
        if (disableChat) {
            return;
        }
        // Setting window.pylon to undefined causes pylon the hide the chat bubble
        let email = account?.email;
        if (!email) {
            email = `unauthenticated-${window.localStorage.getItem(pylonUnauthenticatedUserIdKey)}@example.com`;
        }

        window.pylon = {
            chat_settings: {
                app_id: APP_ID,
                email: email,
                name: account?.name ?? "Unauthenticated",
                avatar_url: account?.avatarUrl,
                account_external_id: account?.id,
            },
        };
        if (window.Pylon) {
            // Fields need to be first configured in Pylon
            window.Pylon("setNewIssueCustomFields", {
                product: "Flex",
            });
        }
    }, [account]);
};

export const useNewInAppChatMessage = () => {
    return (message: string) => {
        if (disableChat) {
            return;
        }
        if (window.Pylon) {
            window.Pylon("showNewMessage", message);
        }
    };
};

export const useResetPylon = () => {
    return () => {
        if (disableChat) {
            return;
        }
        window.pylon = undefined;
        window.localStorage.removeItem(pylonUnauthenticatedUserIdKey);
    };
};

const disableAnimations = async () => {
    try {
        const findAnimations = () => {
            const chatWindow = document.getElementsByClassName("PylonChat-chatWindow")[0];
            if (chatWindow && chatWindow.tagName === "IFRAME") {
                const iframeDoc = (chatWindow as HTMLIFrameElement).contentDocument;
                return iframeDoc?.getElementsByClassName("PylonChat-backgroundBlob--container")[0];
            }
        };

        for (let i = 0; i < 10; i++) {
            const animations = findAnimations();
            if (animations) {
                animations.remove();
                console.log("Animations removed");
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    } catch (error) {
        console.error("Failed to disable animations", error);
    }
};
